import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

export default function AchievementBadge({ badge, compact = false }) {
  const { name, description, emoji, unlocked, progress, maxProgress } = badge;
  const hasProgress = typeof progress === "number" && typeof maxProgress === "number";
  const progressPercent = hasProgress ? (progress / maxProgress) * 100 : 0;

  if (compact) {
    return (
      <motion.div
        whileTap={{ scale: 0.95 }}
        className="flex flex-col items-center gap-1.5 p-2"
      >
        <div className="relative w-14 h-14 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: unlocked ? C.green + "12" : C.border + "40",
            border: `2px solid ${unlocked ? C.green : C.border}`,
          }}>
          {unlocked ? (
            <span className="text-2xl">{emoji}</span>
          ) : (
            <Lock size={18} style={{ color: C.textMuted }} />
          )}
          {unlocked && (
            <motion.div
              className="absolute inset-0 rounded-full"
              initial={{ opacity: 0.6 }}
              animate={{ opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 3, repeat: Infinity }}
              style={{ background: `radial-gradient(circle, ${C.green}15 0%, transparent 70%)` }}
            />
          )}
        </div>
        <span className="text-[10px] font-semibold text-center leading-tight"
          style={{ color: unlocked ? C.text : C.textMuted, maxWidth: 70 }}>
          {name}
        </span>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      className="flex items-center gap-4 p-4"
      style={{
        borderRadius: 18,
        background: C.bgCard,
        boxShadow: BRAND.shadows.sm,
        opacity: unlocked ? 1 : 0.65,
      }}
    >
      {/* Badge Icon */}
      <div className="relative w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: unlocked ? C.green + "12" : C.bg,
          border: `2px solid ${unlocked ? C.green : C.border}`,
        }}>
        {unlocked ? (
          <span className="text-2xl">{emoji}</span>
        ) : (
          <Lock size={20} style={{ color: C.textMuted }} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="text-[14px] font-bold"
          style={{ color: unlocked ? C.text : C.textMuted, fontFamily: BRAND.typography.heading }}>
          {name}
        </h4>
        <p className="text-[12px] leading-snug mt-0.5"
          style={{ color: C.textMuted }}>
          {description}
        </p>

        {/* Progress Bar */}
        {hasProgress && !unlocked && (
          <div className="mt-2">
            <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: C.border }}>
              <motion.div
                className="h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.6 }}
                style={{ backgroundColor: C.green }}
              />
            </div>
            <p className="text-[10px] mt-1" style={{ color: C.textMuted }}>
              {progress}/{maxProgress}
            </p>
          </div>
        )}
      </div>

      {/* Status */}
      {unlocked && (
        <span className="text-[11px] font-semibold px-2 py-1 rounded-full flex-shrink-0"
          style={{ backgroundColor: C.green + "12", color: C.green }}>
          &#10003;
        </span>
      )}
    </motion.div>
  );
}
